/**
 * RoteNummerFahrtenbuchPage — Jun-2026 (User-Wunsch).
 *
 * Eine Seite mit zwei Tabs:
 *   1. Rote Kennzeichen (Probefahrtkennzeichen) verwalten — mehrere möglich;
 *      werden im Probefahrt-Formular zur Auswahl angeboten.
 *   2. Fahrtenbuch ansehen/bearbeiten/exportieren — Einträge entstehen automatisch
 *      aus Probefahrten (source=probefahrt) oder manuell (z. B. Überführungen).
 */
import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Pencil, Check, X, Download, FileText, BadgeCheck, BookOpen, AlertTriangle } from "lucide-react";
import api, { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";

const fmtKm = (v) => (v == null || v === "" ? "—" : `${Number(v).toLocaleString("de-DE")} km`);
const fmtDate = (v) => { if (!v) return null; try { return new Date(v).toLocaleDateString("de-DE"); } catch { return v; } };

// Tage bis Ablauf (negativ = abgelaufen). Rotes Heft: Warnung ab 28 Tagen.
function expiryStatus(dateStr) {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T00:00:00");
    if (isNaN(d)) return null;
    const days = Math.ceil((d - new Date(new Date().toDateString())) / 86400000);
    if (days < 0) return { days, level: "expired", label: `abgelaufen (${fmtDate(dateStr)})` };
    if (days <= 28) return { days, level: "soon", label: `läuft ab in ${days} Tg. (${fmtDate(dateStr)})` };
    return { days, level: "ok", label: `gültig bis ${fmtDate(dateStr)}` };
}

function RedPlatesTab({ plates, reload }) {
    const [plate, setPlate] = useState("");
    const [note, setNote] = useState("");
    const [expiry, setExpiry] = useState("");
    const [editing, setEditing] = useState(null); // {id, plate, note, expiry_date}

    const add = async () => {
        if (!plate.trim()) { toast.error("Bitte ein Kennzeichen eingeben."); return; }
        try {
            await api.post("/dealer/red-plates", { plate: plate.trim(), note: note.trim(), expiry_date: expiry || null });
            setPlate(""); setNote(""); setExpiry(""); reload();
            toast.success("Rotes Kennzeichen hinzugefügt");
        } catch (e) { toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen"); }
    };
    const saveEdit = async () => {
        try {
            await api.patch(`/dealer/red-plates/${editing.id}`, { plate: editing.plate, note: editing.note, expiry_date: editing.expiry_date || null });
            setEditing(null); reload(); toast.success("Aktualisiert");
        } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    };
    const toggleActive = async (p) => {
        try { await api.patch(`/dealer/red-plates/${p.id}`, { active: !p.active }); reload(); }
        catch { toast.error("Änderung fehlgeschlagen"); }
    };
    const remove = async (id) => {
        try { await api.delete(`/dealer/red-plates/${id}`); reload(); toast.success("Gelöscht"); }
        catch { toast.error("Löschen fehlgeschlagen"); }
    };

    return (
        <div className="space-y-5" data-testid="red-plates-tab">
            <div className="flex flex-wrap items-end gap-3 bg-gray-50 border border-gray-200 rounded-sm p-4">
                <label className="flex flex-col gap-1 text-[11px] font-bold uppercase text-gray-500">
                    Probefahrtkennzeichen (Rote Nummer)
                    <Input value={plate} onChange={(e) => setPlate(e.target.value)} placeholder="06-XXXXX"
                        className="rounded-sm font-mono-num uppercase w-48" data-testid="red-plate-input" />
                </label>
                <label className="flex flex-col gap-1 text-[11px] font-bold uppercase text-gray-500">
                    Ablaufdatum Rotes Heft
                    <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                        className="rounded-sm w-44" data-testid="red-plate-expiry-input" />
                </label>
                <label className="flex flex-col gap-1 text-[11px] font-bold uppercase text-gray-500 flex-1 min-w-[180px]">
                    Notiz (optional)
                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Werkstatt / Filiale"
                        className="rounded-sm" data-testid="red-plate-note-input" />
                </label>
                <Button onClick={add} className="rounded-sm gap-1.5 font-bold bg-[#111827] hover:bg-black text-white" data-testid="red-plate-add">
                    <Plus className="w-4 h-4" /> Hinzufügen
                </Button>
            </div>

            {plates.length === 0 ? (
                <p className="text-sm text-gray-400 py-6 text-center" data-testid="red-plates-empty">Noch keine roten Kennzeichen hinterlegt.</p>
            ) : (
                <ul className="divide-y divide-gray-100 border border-gray-200 rounded-sm" data-testid="red-plates-list">
                    {plates.map((p) => {
                        const exp = expiryStatus(p.expiry_date);
                        return (
                        <li key={p.id} className="flex items-center gap-3 p-3 flex-wrap" data-testid={`red-plate-row-${p.id}`}>
                            {editing?.id === p.id ? (
                                <>
                                    <Input value={editing.plate} onChange={(e) => setEditing((s) => ({ ...s, plate: e.target.value }))}
                                        className="rounded-sm font-mono-num uppercase w-40" data-testid={`red-plate-edit-plate-${p.id}`} />
                                    <Input type="date" value={editing.expiry_date || ""} onChange={(e) => setEditing((s) => ({ ...s, expiry_date: e.target.value }))}
                                        className="rounded-sm w-40" data-testid={`red-plate-edit-expiry-${p.id}`} />
                                    <Input value={editing.note || ""} onChange={(e) => setEditing((s) => ({ ...s, note: e.target.value }))}
                                        className="rounded-sm flex-1" placeholder="Notiz" data-testid={`red-plate-edit-note-${p.id}`} />
                                    <Button size="sm" onClick={saveEdit} className="rounded-sm gap-1 bg-[#111827] text-white" data-testid={`red-plate-save-${p.id}`}><Check className="w-4 h-4" /></Button>
                                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
                                </>
                            ) : (
                                <>
                                    <span className="font-mono-num font-black text-base tracking-wide">{p.plate}</span>
                                    {p.active
                                        ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">aktiv</Badge>
                                        : <Badge className="bg-gray-100 text-gray-400 border-gray-200 text-[10px]">inaktiv</Badge>}
                                    {exp && (
                                        <Badge data-testid={`red-plate-expiry-badge-${p.id}`}
                                            className={`text-[10px] ${exp.level === "expired" ? "bg-red-50 text-red-700 border-red-200"
                                                : exp.level === "soon" ? "bg-amber-50 text-amber-700 border-amber-200"
                                                    : "bg-gray-50 text-gray-500 border-gray-200"}`}>
                                            {exp.label}
                                        </Badge>
                                    )}
                                    <span className="flex-1 text-sm text-gray-500 truncate">{p.note || "—"}</span>
                                    <Switch checked={!!p.active} onCheckedChange={() => toggleActive(p)} data-testid={`red-plate-toggle-${p.id}`} />
                                    <button onClick={() => setEditing({ id: p.id, plate: p.plate, note: p.note, expiry_date: p.expiry_date || "" })} className="text-gray-300 hover:text-[#111827]" data-testid={`red-plate-edit-${p.id}`}><Pencil className="w-4 h-4" /></button>
                                    <button onClick={() => remove(p.id)} className="text-gray-300 hover:text-red-600" data-testid={`red-plate-del-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                                </>
                            )}
                        </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

function FahrtenbuchTab({ plates }) {
    const [entries, setEntries] = useState([]);
    const [filterPlate, setFilterPlate] = useState("");
    const [adding, setAdding] = useState(false);
    const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), driver_name: "", vehicle_label: "", plate: "", purpose: "", km_start: "", km_end: "" });
    const [editKm, setEditKm] = useState(null); // {id, km_end}

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/dealer/fahrtenbuch", { params: filterPlate ? { plate: filterPlate } : {} });
            setEntries(data?.items || []);
        } catch { /* ignore */ }
    }, [filterPlate]);
    useEffect(() => { load(); }, [load]);

    const addManual = async () => {
        if (!form.date || !form.driver_name.trim()) { toast.error("Datum und Fahrer sind erforderlich."); return; }
        try {
            await api.post("/dealer/fahrtenbuch", {
                ...form,
                km_start: form.km_start === "" ? null : Number(form.km_start),
                km_end: form.km_end === "" ? null : Number(form.km_end),
            });
            setAdding(false);
            setForm({ date: new Date().toISOString().slice(0, 10), driver_name: "", vehicle_label: "", plate: "", purpose: "", km_start: "", km_end: "" });
            load(); toast.success("Eintrag hinzugefügt");
        } catch (e) { toast.error(e.response?.data?.detail || "Anlegen fehlgeschlagen"); }
    };
    const saveKm = async () => {
        try {
            await api.patch(`/dealer/fahrtenbuch/${editKm.id}`, { km_end: editKm.km_end === "" ? null : Number(editKm.km_end) });
            setEditKm(null); load(); toast.success("km-Stand aktualisiert");
        } catch { toast.error("Speichern fehlgeschlagen"); }
    };
    const remove = async (id) => {
        try { await api.delete(`/dealer/fahrtenbuch/${id}`); load(); toast.success("Eintrag gelöscht"); }
        catch { toast.error("Löschen fehlgeschlagen"); }
    };
    const exportCsv = () => {
        const token = localStorage.getItem("token");
        const url = `${API}/dealer/fahrtenbuch/export.csv${filterPlate ? `?plate=${encodeURIComponent(filterPlate)}` : ""}`;
        // Mit Auth-Header laden und als Blob herunterladen
        api.get(url.replace(API, ""), { responseType: "blob" }).then((r) => {
            const u = URL.createObjectURL(r.data);
            const a = document.createElement("a"); a.href = u; a.download = "Fahrtenbuch.csv";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
        }).catch(() => toast.error("Export fehlgeschlagen"));
        void token;
    };

    return (
        <div className="space-y-4" data-testid="fahrtenbuch-tab">
            <div className="flex flex-wrap items-center gap-3">
                <select value={filterPlate} onChange={(e) => setFilterPlate(e.target.value)}
                    className="border border-gray-300 rounded-sm px-3 py-2 text-sm" data-testid="fahrtenbuch-filter-plate">
                    <option value="">Alle Kennzeichen</option>
                    {plates.map((p) => <option key={p.id} value={p.plate}>{p.plate}</option>)}
                </select>
                <div className="flex-1" />
                <Button onClick={() => setAdding((v) => !v)} variant="outline" className="rounded-sm gap-1.5 font-bold" data-testid="fahrtenbuch-add-toggle">
                    <Plus className="w-4 h-4" /> Manueller Eintrag
                </Button>
                <Button onClick={exportCsv} className="rounded-sm gap-1.5 font-bold bg-[#111827] hover:bg-black text-white" data-testid="fahrtenbuch-export">
                    <Download className="w-4 h-4" /> CSV-Export
                </Button>
            </div>

            {entries.filter((e) => e.km_end == null).length > 0 && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-3 py-2 text-sm" data-testid="fahrtenbuch-missing-km-banner">
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    <span><strong>{entries.filter((e) => e.km_end == null).length}</strong> Eintrag/Einträge ohne km-Ende — bitte km-Stand nachtragen (für lückenlose Audits).</span>
                </div>
            )}

            {adding && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-50 border border-gray-200 rounded-sm p-4" data-testid="fahrtenbuch-add-form">
                    <FbField label="Datum"><Input type="date" value={form.date} onChange={(e) => setForm((s) => ({ ...s, date: e.target.value }))} className="rounded-sm" data-testid="fb-date" /></FbField>
                    <FbField label="Fahrer"><Input value={form.driver_name} onChange={(e) => setForm((s) => ({ ...s, driver_name: e.target.value }))} className="rounded-sm" data-testid="fb-driver" /></FbField>
                    <FbField label="Fahrzeug"><Input value={form.vehicle_label} onChange={(e) => setForm((s) => ({ ...s, vehicle_label: e.target.value }))} className="rounded-sm" data-testid="fb-vehicle" /></FbField>
                    <FbField label="Kennzeichen">
                        <select value={form.plate} onChange={(e) => setForm((s) => ({ ...s, plate: e.target.value }))} className="border border-gray-300 rounded-sm px-2 py-2 text-sm w-full" data-testid="fb-plate">
                            <option value="">—</option>
                            {plates.map((p) => <option key={p.id} value={p.plate}>{p.plate}</option>)}
                        </select>
                    </FbField>
                    <FbField label="Zweck"><Input value={form.purpose} onChange={(e) => setForm((s) => ({ ...s, purpose: e.target.value }))} placeholder="z. B. Überführung" className="rounded-sm" data-testid="fb-purpose" /></FbField>
                    <FbField label="km Start"><Input type="number" value={form.km_start} onChange={(e) => setForm((s) => ({ ...s, km_start: e.target.value }))} className="rounded-sm font-mono-num" data-testid="fb-km-start" /></FbField>
                    <FbField label="km Ende"><Input type="number" value={form.km_end} onChange={(e) => setForm((s) => ({ ...s, km_end: e.target.value }))} className="rounded-sm font-mono-num" data-testid="fb-km-end" /></FbField>
                    <div className="flex items-end">
                        <Button onClick={addManual} className="rounded-sm gap-1 font-bold bg-[#111827] text-white w-full" data-testid="fb-save">Speichern</Button>
                    </div>
                </div>
            )}

            {entries.length === 0 ? (
                <p className="text-sm text-gray-400 py-8 text-center" data-testid="fahrtenbuch-empty">Noch keine Fahrtenbuch-Einträge.</p>
            ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-sm">
                    <table className="w-full text-sm" data-testid="fahrtenbuch-table">
                        <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                            <tr>
                                <th className="text-left p-2">Datum</th>
                                <th className="text-left p-2">Fahrer</th>
                                <th className="text-left p-2">Fahrzeug</th>
                                <th className="text-left p-2">Kennz.</th>
                                <th className="text-left p-2">Zweck</th>
                                <th className="text-right p-2">km Start</th>
                                <th className="text-right p-2">km Ende</th>
                                <th className="text-right p-2">gefahren</th>
                                <th className="text-left p-2">Quelle</th>
                                <th className="p-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((e) => {
                                const missingKm = e.km_end == null;
                                return (
                                <tr key={e.id} className={`border-t border-gray-100 ${missingKm ? "bg-amber-50" : ""}`} data-testid={`fb-row-${e.id}`}>
                                    <td className="p-2 whitespace-nowrap">{e.date}</td>
                                    <td className="p-2">{e.driver_name}</td>
                                    <td className="p-2 max-w-[180px] truncate">{e.vehicle_label || "—"}</td>
                                    <td className="p-2 font-mono-num">{e.plate || "—"}</td>
                                    <td className="p-2">{e.purpose || "—"}</td>
                                    <td className="p-2 text-right font-mono-num">{fmtKm(e.km_start)}</td>
                                    <td className="p-2 text-right font-mono-num">
                                        {editKm?.id === e.id ? (
                                            <span className="inline-flex items-center gap-1">
                                                <Input type="number" value={editKm.km_end} onChange={(ev) => setEditKm((s) => ({ ...s, km_end: ev.target.value }))} className="rounded-sm w-24 h-7 font-mono-num" data-testid={`fb-edit-km-${e.id}`} />
                                                <button onClick={saveKm} className="text-emerald-600" data-testid={`fb-save-km-${e.id}`}><Check className="w-4 h-4" /></button>
                                                <button onClick={() => setEditKm(null)} className="text-gray-400"><X className="w-4 h-4" /></button>
                                            </span>
                                        ) : missingKm ? (
                                            <button onClick={() => setEditKm({ id: e.id, km_end: "" })} className="inline-flex items-center gap-1 text-amber-700 font-bold hover:text-amber-900" data-testid={`fb-editkm-btn-${e.id}`}>
                                                <AlertTriangle className="w-3.5 h-3.5" /> km-Stand nachtragen
                                            </button>
                                        ) : (
                                            <button onClick={() => setEditKm({ id: e.id, km_end: e.km_end ?? "" })} className="hover:text-[#111827]" data-testid={`fb-editkm-btn-${e.id}`}>{fmtKm(e.km_end)}</button>
                                        )}
                                    </td>
                                    <td className="p-2 text-right font-mono-num font-bold">{e.driven_km != null ? `${e.driven_km} km` : "—"}</td>
                                    <td className="p-2">
                                        {e.source === "probefahrt"
                                            ? <Badge className="bg-[#FFCC00]/15 text-[#7a6500] border-[#FFCC00]/40 text-[10px]">Probefahrt</Badge>
                                            : <Badge className="bg-gray-100 text-gray-500 border-gray-200 text-[10px]">Manuell</Badge>}
                                    </td>
                                    <td className="p-2 text-right">
                                        {e.source === "manual" && (
                                            <button onClick={() => remove(e.id)} className="text-gray-300 hover:text-red-600" data-testid={`fb-del-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const FbField = ({ label, children }) => (
    <label className="flex flex-col gap-1 text-[11px] font-bold uppercase text-gray-500">{label}{children}</label>
);

export default function RoteNummerFahrtenbuchPage() {
    const [plates, setPlates] = useState([]);
    const reloadPlates = useCallback(async () => {
        try { const { data } = await api.get("/dealer/red-plates"); setPlates(data?.items || []); }
        catch { /* ignore */ }
    }, []);
    useEffect(() => { reloadPlates(); }, [reloadPlates]);

    return (
        <div className="max-w-5xl mx-auto px-4 py-8" data-testid="rote-nummer-fahrtenbuch-page">
            <div className="flex items-center gap-2 mb-1">
                <BadgeCheck className="w-6 h-6 text-[#111827]" />
                <h1 className="text-2xl font-black text-[#111827]">Rote Nummer & Fahrtenbuch</h1>
            </div>
            <p className="text-sm text-gray-500 mb-6">Probefahrtkennzeichen verwalten und Fahrten dokumentieren (Probefahrten automatisch, Überführungen manuell).</p>

            <Tabs defaultValue="plates">
                <TabsList className="mb-6">
                    <TabsTrigger value="plates" className="gap-1.5" data-testid="tab-red-plates"><FileText className="w-4 h-4" /> Rote Kennzeichen</TabsTrigger>
                    <TabsTrigger value="fahrtenbuch" className="gap-1.5" data-testid="tab-fahrtenbuch"><BookOpen className="w-4 h-4" /> Fahrtenbuch</TabsTrigger>
                </TabsList>
                <TabsContent value="plates"><RedPlatesTab plates={plates} reload={reloadPlates} /></TabsContent>
                <TabsContent value="fahrtenbuch"><FahrtenbuchTab plates={plates} /></TabsContent>
            </Tabs>
        </div>
    );
}
